<template>
  <div
    id="knowledge-item-detail-view"
    class="my-4 mx-md-5 mx-lg-10 mx-xl-10 pa-2 pt-md-0 px-md-4 mt-0 maxw-1750"
  >
    <a @click="hideNavInternal()" v-if="navInternal">
      <v-icon color="primary" size="24" class="iconNavInternal">
        mdi-arrow-left
      </v-icon>
    </a>
    <!-- loader skeleton -->
    <template v-if="loading">
      <v-row no-gutters class="flex-row flex-nowrap mt-4">
        <v-skeleton-loader type="heading" width="100%" />
      </v-row>
      <v-card v-bind:class="['mt-4 py-4', {
        'px-2': !$vuetify.breakpoint.mdAndUp,
        'px-4': $vuetify.breakpoint.mdAndUp
      }]">
        <v-skeleton-loader type="text" />
        <v-skeleton-loader class="mt-4" type="paragraph" />
      </v-card>
    </template>

    <template v-else-if="itemData != null">
      <div
        :class="['section-mh d-flex flex-nowrap align-center justify-start',
          $vuetify.breakpoint.mdAndUp ? 'flex-row' : 'flex-column'
        ]"
        style="max-width: 100%"
      >
        <div
          class="d-flex flex-row flex-nowrap"
          :style="{
              width: $vuetify.breakpoint.mdAndUp ? 'initial': '100%',
          }"
        >

          <h2
            id="title"
            :class="['mb-0 p-0',
              `mr-${$vuetify.breakpoint.mdAndUp ? '4' : '0'}`,
            ]"
            :style="{
              wordBreak: 'break-word',
              width: $vuetify.breakpoint.mdAndUp ? 'initial': '100%',
            }"
          >{{ itemData.name }}</h2>
        </div>

        <v-spacer
          class="middle-hr my-2 my-md-0"
          :style="{
            borderColor: $vuetify.theme.currentTheme.primary,
            width: $vuetify.breakpoint.mdAndUp ? 'initial' : '100%'
          }"
        />

        <div
          v-if="$vuetify.breakpoint.mdAndUp"
          class="ki-actions d-flex flex-row flex-nowrap justify-end col-auto px-0"
          :style="{ borderColor: $vuetify.theme.currentTheme.primary }"
        >

          <v-btn
            v-for="(action, i) in actions"
            :key="i"
            icon
            class="ml-4"
            @click.stop="viewAction($event, action)"
            @mouseover="actionHovered = action.name"
            @mouseleave="actionHovered = null"
          >
            <v-icon>{{ action.icon }}</v-icon>

            <!-- if action name has 11 or more characters we will adjust
              the last action name from the right to prevent the text out of screen -->
            <div :class="['ki-action-name', {
              'right': $t(`views.knowledgeItems.${action.name}.title`).length > 11
            }]">
              <v-scroll-y-transition>
                <p
                  class="text-body mb-0"
                >{{ $t(`views.knowledgeItems.${action.name}.title`) }}</p>
              </v-scroll-y-transition>
            </div>
          </v-btn>
        </div>
      </div>

      <div :class="['d-flex justify-start',
        $vuetify.breakpoint.smAndUp ? 'flex-row align-center' : 'flex-column',
      ]">
        <div>
          <small class="mr-2 mt-2" v-if="itemData.publishedDate != null">
            {{
              $t("views.knowledgeItems.publishedDate", [
                $date("format", itemData.publishedDate),
              ])
            }}
          </small>
          <small class="mr-4 mt-2" v-if="itemData.modifyDate != null">
            {{
              $t("views.knowledgeItems.modifyDate", [
                $date("format", itemData.modifyDate),
              ])
            }}
          </small>
        </div>
        <div
          :style="{display: 'flex'}">
          <v-rating
            v-if="itemData.avgRating != null"
            half-increments
            length="5"
            :readonly="true"
            :value="itemData.avgRating"
          >
            <template v-slot:item="{
              index, isFilled, isHalfFilled,
            }">
              <v-icon
                x-small
                color="primary"
                :class="['pa-0', { 'ml-1': index > 0 }]"
                v-text="`mdi-star${!isFilled ? (
                  isHalfFilled ? '-half-full' : '-outline'
                ) : ''}`"
              ></v-icon>
            </template>
          </v-rating>
          <small class="ml-3"
            :style="{marginTop: '3px'}"
            v-if="itemData.avgRating != null">
            {{ (itemData.vote_count).toString().replace(/\B(?=(\d{3})+\b)/g, '.') }}
            {{
              itemData.vote_count > 1
              ? $t("views.knowledgeItems.votes")
              : $t("views.knowledgeItems.vote")
            }}
          </small>
        </div>
      </div>

      <!-- rate action component -->
      <v-expand-transition>
        <v-card
          v-if="currentAction != null && currentAction.name === 'rate'"
          class="mt-4 pa-4"
        >
          <div class="d-flex flex-column flex-nowrap">
            <label>{{ $t('views.knowledgeItems.rate.solvingQuestion.question') }}</label>
            <v-btn-toggle
              class="mt-2"
              v-model="solvingQuestionAnswer"
              mandatory
            >
              <v-btn :disabled="sendingRating" color="secundary">
                {{ $t('views.knowledgeItems.rate.solvingQuestion.yes') }}
              </v-btn>
              <v-btn :disabled="sendingRating" color="secundary">
                {{ $t('views.knowledgeItems.rate.solvingQuestion.no') }}
              </v-btn>
            </v-btn-toggle>

            <label class="mt-4">
              {{ $t('views.knowledgeItems.rate.ratingQuestion.question') }}
            </label>
            <v-btn-toggle
              class="mt-2"
              v-model="ratingQuestionAnswer"
              mandatory
            >
              <v-btn :disabled="sendingRating" color="secundary">
                {{ $t('views.knowledgeItems.rate.ratingQuestion.much') }}
              </v-btn>
              <v-btn :disabled="sendingRating" color="secundary">
                {{ $t('views.knowledgeItems.rate.ratingQuestion.little') }}
              </v-btn>
              <v-btn :disabled="sendingRating" color="secundary">
                {{ $t('views.knowledgeItems.rate.ratingQuestion.none') }}
              </v-btn>
            </v-btn-toggle>

            <div>
              <v-textarea
                class="mt-4"
                v-model="comment"
                outlined
                hide-details="auto"
                :disabled="sendingRating"
                :auto-grow="true"
                :no-resize="!$vuetify.breakpoint.mdAndUp"
                :rows="$vuetify.breakpoint.mdAndUp ? 5 : 12"
                :autofocus="true"
                :placeholder="$t('views.knowledgeItems.rate.commentPlaceholder')"
              />
            </div>

            <div class="d-flex justify-end">
              <v-btn
                class="mt-4 mr-4"
                color="primary"
                right
                dark
                :loading="sendingRating"
                @click.stop="rateKnowledgeItem"
              >{{ $t('views.knowledgeItems.rate.send') }}
              </v-btn>
              <v-btn
                class="mt-4"
                right
                :disabled="sendingRating"
                @click.stop="currentAction = null"
              >{{ $t('views.knowledgeItems.rate.cancel') }}
              </v-btn>
            </div>
          </div>
        </v-card>
      </v-expand-transition>

      <v-card class="mt-4" v-show="user.isUserTecnical">
        <v-tabs>
          <v-tab v-if="!isEmpty(itemData.resolution)" class="firstTab">
            {{ $t('views.knowledgeItems.technicalTab') }}
          </v-tab>
          <v-tab v-if="itemData.zinfo_usuariofinal" class="secondTab">
            {{ $t('views.knowledgeItems.userTab') }}
          </v-tab>
          <v-tab-item v-if="!isEmpty(itemData.resolution) && !secondTab">
            <v-card>
              <v-card-text
                class="body-1 black--text resolution--content"
                v-html="parseResolution(itemData.resolution)"
              >
              </v-card-text>
            </v-card>
          </v-tab-item>
          <v-tab-item v-if="itemData.zinfo_usuariofinal">
            <v-card>
                <v-card-text
                  class="body-1 black--text resolution--content"
                  v-html="parseResolution(itemData.zinfo_usuariofinal)"
                >
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs>
      </v-card>

      <v-card class="mt-4" v-show="!user.isUserTecnical">
        <v-card>
          <v-card-text
            class="body-1 black--text resolution--content"
            v-html="parseResolution(itemData.zinfo_usuariofinal)"
          >
          </v-card-text>
        </v-card>
      </v-card>

      <v-card class="mt-4 pa-4">
        <RateComponent
          :pregunta="$t('views.knowledgeItems.rate.rateTitle')"
          :id="itemData.id"
          codigo="KD"
          :canal=1
        />
      </v-card>

      <ShareDialog
        :visible="currentAction != null && currentAction.name === 'share' && !isMobile"
        @input="currentAction = null"
        :item="sharedItem"
        :positionX="sharedDialogPosition.positionX"
        :positionY="sharedDialogPosition.positionY"
      ></ShareDialog>
    </template>

    <template v-else>
      <h2>{{ $t('views.knowledgeItems.listTitle') }}</h2>
      <v-card class="mt-4">
        <v-card-text class="text-subtitle-2">
          {{ $t('views.knowledgeItems.notFound') }}
        </v-card-text>
      </v-card>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import { ShareDialog } from '@/components';
import store from '@/store';
import RateComponent from '../components/RateComponent.vue';

export default {
  props: {
    id: {
      type: [String, Number],
      required: true,
    },
    backButton: Boolean,
    loading: Boolean,
    item: Object,
    publicView: {
      type: Boolean,
      default: () => false,
    },
    internalAccess: Boolean,
  },
  components: { ShareDialog, RateComponent },
  data: () => ({
    client: null,
    itemData: null,
    currentAction: null,
    actionHovered: null,
    comment: null,
    sendingRating: false,
    secondTab: false,
    sharedDialogPosition: {},
  }),
  beforeRouteUpdate(to, from, next) {
    this.sendingRating = false;
    this.comment = null;
    if ([from.name, to.name].every((name) => name === 'KnowledgeItemsDetailView')) {
      this.getItem(to.params.id);
    }
    // on route change we will close rating form
    this.currentAction = null;
    next();
  },
  methods: {
    getItem(id) {
      // eslint-disable-next-line radix
      const parsedId = parseInt(id);
      this.$emit('request', { type: 'detail', args: { id: parsedId } });
    },
    viewAction(evt, action) {
      if (this.currentAction == null || this.currentAction.name !== action.name) {
        this.currentAction = action;
        if (action.name === 'share') {
          this.share(evt);
        }
      } else {
        this.currentAction = null;
      }
    },
    share(evt) {
      const isMobile = this.$store.getters['globalState/get']('deviceOS') != null;
      if (isMobile === true && window.flutter_inappwebview) {
        const args = {
          name: this.$t('components.share.mobile.knowledgeItems'),
          url: `${process.env.VUE_APP_EXTERNAL_URL}${process.env.VUE_APP_BASE_URL_MICS_SSPA}knowledge-items/${this.itemData.id}`,
          formName: this.itemData.name,
          type: 'KI',
        };
        window.flutter_inappwebview.callHandler('shareDataUsingNative', args);
      } else {
        const closestButton = evt.target.closest('button');
        const boundingRect = closestButton.getBoundingClientRect();

        this.sharedDialogPosition = {
          positionX: boundingRect.left + (closestButton.offsetWidth / 2),
          positionY: boundingRect.top + (closestButton.offsetHeight / 2),
        };
      }
    },
    parseResolution(resolution) {
      const accessKey = (this.$store.getters['session/storage'] || {}).access_key;
      if (typeof resolution !== 'string' || accessKey == null) {
        return resolution;
      }
      return resolution
        .replace(/<img[^>]+src="([^">]+)"/g, (img, src) => img.replace(src, `${src}?accessKey=${accessKey}`));
    },
    isEmpty(resolution) {
      if (resolution === null || resolution === undefined || resolution === '') {
        return true;
      }
      // Replace all the \n and \r characters from string and check if it is empty
      const resolutionWithoutLineBreaks = resolution.replace(/(\r\n|\n|\r)/gm, '');
      return resolutionWithoutLineBreaks.trim() === '';
    },
    hideNavInternal() {
      store.dispatch('routeState/setCurrentPath', '');
      store.dispatch('routeState/setCurrentPathId', '');
      store.dispatch('routeState/setNavInternal', false);
    },
  },
  computed: {
    ...mapState({
      user: ({ session: { session } }) => (session != null ? session.user : null),
    }),
    ...mapGetters({
      navInternal: 'routeState/getNavInternal',
    }),
    actions() {
      const actions = [{
        icon: 'mdi-share-variant', name: 'share',
      }];

      return actions;
    },
    sharedItem() {
      const knowledgeUrl = 'knowledge-items/';
      return {
        text: `${this.$t('components.share.subjectBody.knowledge')} ${this.itemData.name}`,
        url: `${process.env.VUE_APP_EXTERNAL_URL}${process.env.VUE_APP_BASE_URL_MICS_SSPA}${knowledgeUrl}${this.id}`,
        formName: this.itemData.name,
        type: 'KI',
      };
    },
    isMobile() {
      const getter = this.$store.getters['globalState/get'];
      return getter('deviceOS') != null;
    },
  },
  created() {
    this.client = this.$api.getClient('knowledgeItems');
    if (this.internalAccess || this.itemData === null) {
      this.loading = true;
      const baseUrl = this.$router.options.base;
      const req = this.client.getDetail(this.id, baseUrl, this.publicView);
      req.then((response) => {
        const data = req.format(response).unique();
        this.itemData = data;
        this.loading = false;
      });
    }
  },
  mounted() {
    if (this.publicView && this.$store.getters['session/isSignedIn']) {
      this.$router.push(`/knowledge-items/${this.id}`);
    } else {
      this.getItem(this.id);
    }

    this.$root.$on('setItemData', (item) => {
      this.itemData = item;
    });
  },
};
</script>

<style lang="scss">
  #knowledge-item-detail-view {
    .v-speed-dial__list {
      align-items: flex-end;
    }

    .resolution--content {
      img {
        max-width: 100%;
      }
    }
  }
  .clickedTab {
    color: #757575 !important;
  }
  .clickedTab2 {
    color: #008d6d !important;
    border-bottom: 2px solid #008d6d;
  }
  .noColorState {
    color: transparent !important;
  }
  .selectNegative {
    //max-width: 40%;
  }
</style>

<style lang="scss" scoped>
  $ki-action-button-size: 55px;
  $ki-action-bottom: -45px;
  $ki-action-last-item-right: -40px;
  $ki-action-last-item-right-mdandup: -50px;

  #title{
    -webkit-hyphens: auto;
    -moz-hyphens: auto;
    hyphens: auto;
  }

  .middle-hr {
    border-color: inherit;
    border-width: 1px 0;
    border-style: solid;
  }

  .ki-actions {
    position: relative;

    > button {
      width: $ki-action-button-size;
      height: $ki-action-button-size;
      border-radius: 50%;
      border-color: inherit;
      border-width: 2px;
      border-style: solid;
      background-color: white;

      &:last-child .ki-action-name.right {
        left: 100%;
        display: table;
        transform: translateX(-100%);
      }
    }

    .ki-action-name {
      pointer-events: none;
      position: absolute;
      bottom: $ki-action-bottom;
      left: 50%;
      transform: translateX(-50%);
    }
  }

  .primary--border {
    border-width: 1px;
    border-style: solid;
  }

  button.v-btn.v-item--active.v-btn--active.v-btn--is-elevated{
    background-color: #27846d !important;
  }

  .iconNavInternal {
    margin-top: 20px;
  }

  #floatingLonelyButton {
    position: fixed;
    bottom: 70px;
    right: 16px;
    width: 56px;
    height: 56px;
    border-radius: 100%;
    display: flex;
    flex-direction: column;
    min-width: auto;
  }

  .knowledge__rating  {
    font-size: 1.2rem;
  }
  .icon__start--knowledgeItems {
    color: #27846d;
    margin: 0;
    margin-right: 0.2rem;
    margin-left: -0.2rem;
    font-size: 1.3rem !important;
  }

  .icon-ayudadigital-soluciones:before {
    font-size: larger !important;
  }

  blockquote {
    margin: 0 0 0 20px !important;
  }
</style>
